import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { comicPath } from '../lib/format'
import { ComicPageData } from '../lib/types'
import { useLoad } from '../lib/useLoad'
import { ComicView } from '../ui/ComicView'
import { Layout } from '../ui/Layout'
import { RandomButton } from '../ui/RandomButton'
import { EmptyState, Loading, Sk } from '../ui/states'

function LatestButton() {
  const navigate = useNavigate()
  const go = () =>
    api
      .get<{ slug: string }>('/api/comics/latest')
      .then(({ slug }) => navigate(comicPath(slug)))
      .catch(() => navigate('/'))
  return (
    <button type="button" className="btn solid" onClick={go}>
      Latest comic
    </button>
  )
}

export function ComicPage() {
  const { slug = '' } = useParams()
  const { isArtist } = useAuth()
  const load = useLoad((signal) => api.get<ComicPageData>(`/api/comics/${encodeURIComponent(slug)}`, signal), [slug])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])
  useEffect(() => {
    if (load.state === 'ready') document.title = `${load.data.comic.title} · What a comical life`
  }, [load])

  let body
  if (load.state === 'loading') {
    body = (
      <Loading label="Loading comic">
        <div className="vstack gap-md">
          <Sk w="70%" h={48} />
          <Sk w="30%" h={16} />
          <div className="hstack between mt-sm mb-md">
            <Sk w="120px" h={48} />
            <Sk w="110px" h={48} />
            <Sk w="100px" h={48} />
          </div>
          <Sk ratio="1 / 1" />
          <Sk ratio="1 / 1" />
          <Sk h={18} />
          <Sk w="80%" h={18} />
        </div>
      </Loading>
    )
  } else if (load.state === 'error' && load.error.status === 404) {
    body = (
      <EmptyState
        title="We couldn't find that comic"
        actions={
          <div className="btns">
            <LatestButton />
            <RandomButton label="Random comic" />
            <Link className="btn" to="/">
              Browse all
            </Link>
          </div>
        }
      >
        It may have been moved or unpublished. Try the latest comic, or let fate decide.
      </EmptyState>
    )
  } else if (load.state === 'error') {
    body = (
      <EmptyState
        error
        title="We couldn't load this comic"
        actions={
          <div className="btns">
            <button type="button" className="btn solid" onClick={load.retry}>
              Try again
            </button>
            <Link className="btn" to="/">
              Go to home
            </Link>
          </div>
        }
      >
        The comic didn't load. Check your connection and try again.
      </EmptyState>
    )
  } else {
    const d = load.data
    body = (
      <>
        {isArtist && (
          <div className="artist-bar">
            <span>Logged in as artist</span>
            <Link className="btn solid sm" to={`/artist/edit/${d.comic.id}`}>
              Edit comic
            </Link>
          </div>
        )}
        <ComicView comic={d.comic} previous={d.previous} next={d.next} related={d.related} comments={d.comments} />
      </>
    )
  }

  return (
    <Layout>
      <div className="column">{body}</div>
    </Layout>
  )
}
